// Role-based permission system for the Procurement Management System

export type UserRole = 'admin' | 'approver' | 'quality_inspector' | 'user' | 'procurement_manager';

export type Permission = 
  | 'view_dashboard'
  | 'manage_skus'
  | 'view_skus'
  | 'manage_vendors'
  | 'view_vendors'
  | 'create_purchase_orders'
  | 'approve_purchase_orders'
  | 'view_purchase_orders'
  | 'track_purchase_orders'
  | 'manage_gate_entry'
  | 'view_gate_entry'
  | 'perform_quality_inspection'
  | 'view_quality_reports'
  | 'manage_invoices'
  | 'view_invoices'
  | 'process_payments'
  | 'manage_users'
  | 'system_settings'
  | 'view_reports'
  | 'export_data';

// Define permissions for each role
export const rolePermissions: Record<UserRole, Permission[]> = {
  admin: [
    'view_dashboard',
    'manage_skus',
    'view_skus',
    'manage_vendors',
    'view_vendors',
    'create_purchase_orders',
    'approve_purchase_orders',
    'view_purchase_orders',
    'track_purchase_orders',
    'manage_gate_entry',
    'view_gate_entry',
    'perform_quality_inspection',
    'view_quality_reports',
    'manage_invoices',
    'view_invoices',
    'process_payments',
    'manage_users',
    'system_settings',
    'view_reports',
    'export_data'
  ],
  
  procurement_manager: [
    'view_dashboard',
    'manage_skus',
    'view_skus',
    'manage_vendors',
    'view_vendors',
    'create_purchase_orders',
    'view_purchase_orders',
    'track_purchase_orders',
    'manage_invoices',
    'view_invoices',
    'view_reports',
    'export_data'
  ],
  
  approver: [
    'view_dashboard',
    'view_skus',
    'view_vendors',
    'approve_purchase_orders',
    'view_purchase_orders',
    'track_purchase_orders',
    'view_invoices',
    'process_payments',
    'view_reports'
  ],
  
  quality_inspector: [
    'view_dashboard',
    'view_skus',
    'view_vendors',
    'view_purchase_orders',
    'track_purchase_orders',
    'manage_gate_entry',
    'view_gate_entry',
    'perform_quality_inspection',
    'view_quality_reports',
    'view_reports'
  ],
  
  user: [
    'view_dashboard',
    'view_skus',
    'view_vendors',
    'create_purchase_orders',
    'view_purchase_orders',
    'track_purchase_orders',
    'view_gate_entry',
    'view_quality_reports',
    'view_invoices'
  ]
};

// Helper function to check if a user has a specific permission
export const hasPermission = (userRole: UserRole, permission: Permission): boolean => {
  return rolePermissions[userRole]?.includes(permission) ?? false;
};

// Helper function to check if a user has any of the specified permissions
export const hasAnyPermission = (userRole: UserRole, permissions: Permission[]): boolean => {
  return permissions.some(permission => hasPermission(userRole, permission));
};

// Helper function to check if a user has all of the specified permissions
export const hasAllPermissions = (userRole: UserRole, permissions: Permission[]): boolean => {
  return permissions.every(permission => hasPermission(userRole, permission));
};

// Get all permissions for a role
export const getRolePermissions = (userRole: UserRole): Permission[] => {
  return rolePermissions[userRole] ?? [];
};

// Check if a role can access a specific module
export const canAccessModule = (userRole: UserRole, module: string): boolean => {
  switch (module) {
    case 'skus':
      return hasPermission(userRole, 'view_skus');
    case 'vendors':
      return hasPermission(userRole, 'view_vendors');
    case 'purchase-orders':
      return hasPermission(userRole, 'view_purchase_orders');
    case 'gate-entry':
      return hasPermission(userRole, 'view_gate_entry');
    case 'quality-control':
      return hasAnyPermission(userRole, ['perform_quality_inspection', 'view_quality_reports']);
    case 'invoices':
      return hasPermission(userRole, 'view_invoices');
    case 'users':
      return hasPermission(userRole, 'manage_users');
    case 'settings':
      return hasPermission(userRole, 'system_settings');
    default:
      return true; // Allow access to unknown modules by default
  }
};

// Get user-friendly role name
export const getRoleName = (role: UserRole): string => {
  switch (role) {
    case 'admin':
      return 'Administrator';
    case 'procurement_manager':
      return 'Procurement Manager';
    case 'approver':
      return 'Approver';
    case 'quality_inspector':
      return 'Quality Inspector';
    case 'user':
      return 'User';
    default:
      return role;
  }
};

// Get role description
export const getRoleDescription = (role: UserRole): string => {
  switch (role) {
    case 'admin':
      return 'Full system access with all administrative privileges';
    case 'procurement_manager':
      return 'Manages procurement processes, SKUs, vendors, and purchase orders';
    case 'approver':
      return 'Reviews and approves purchase orders and processes payments';
    case 'quality_inspector':
      return 'Performs quality inspections and manages gate entry';
    case 'user':
      return 'Basic user with limited access to view data and create purchase orders';
    default:
      return 'Standard user role';
  }
};
